<template>
    <div
        class="engagement-ring-stone-recommendation-card"
        :class="{ 'highlighted': highlighted }"
    >
        <h4
            v-if="title"
            class="title"
        >
            {{ title }}
        </h4>

        <StonePreview
            v-if="data.__typename !== 'StoneRecommendationNotFound'"
            :stone="data"
            class="stone-preview"
        >
            <template #details>
                <p
                    v-if="subtitle"
                    class="subtitle"
                >
                    {{ subtitle }}
                </p>
            </template>

            <template #button>
                <ThButton
                    v-qa="`select-recommendation`"
                    color="var(--blue)"
                    size="small"
                    type="solid"
                    @click="select"
                >
                    Select
                </ThButton>
            </template>
        </StonePreview>

        <div v-else-if="content">
            <div
                v-if="data.haveWithoutPricingRange"
                class="no-result"
            >
                <ContentComponentItem
                    v-for="(component, key) in content.notFoundContent"
                    :key
                    :background="'WHITE'"
                    :data="component"
                    :page-type="pageType"
                />
            </div>

            <div
                v-else
                class="no-result"
            >
                <ContentComponentItem
                    v-for="(component, key) in content.notSupportedContent"
                    :key
                    :background="'WHITE'"
                    :data="component"
                    :page-type="pageType"
                />
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import type {
    EngagementRingStoneRecommendationCardFragment,
    StoneRecommendationsNotFoundFragment,
} from '@/graphql/api'
import type { RingBuilderStoneTypes } from '@/domain/product/ring/ring-builder'
import {
    getEngagementRingStoneRecommendationCardSubtitle,
    getEngagementRingStoneRecommendationCardTitle,
} from './helpers'

import ContentComponentItem from '@/components/cms/component/ContentComponentItem.vue'
import StonePreview from '@/components/stone/StonePreview.vue'
import ThButton from '@/components/ui/buttons/ThButton.vue'
import type { PageType } from '@/components/cms/component/content/ContentButton.vue'

const props = defineProps<{
    data: RingBuilderStoneTypes | StoneRecommendationsNotFoundFragment
    content: EngagementRingStoneRecommendationCardFragment | undefined
    pageType: PageType
    highlighted?: boolean
}>()

const emit = defineEmits<{
    select: [stone: RingBuilderStoneTypes]
}>()

const select = () => {
    if (props.data.__typename !== 'StoneRecommendationNotFound')
        emit('select', props.data)
}

const title = computed(() =>
    getEngagementRingStoneRecommendationCardTitle(props.content, props.data)
)

const subtitle = computed(() =>
    getEngagementRingStoneRecommendationCardSubtitle(props.content, props.data)
)
</script>

<style lang="scss" scoped>
.engagement-ring-stone-recommendation-card {
    &.highlighted {
        .stone-preview {
            :deep(.details) {
                background: $sage;
            }
        }

        .title {
            background: $blue;
            color: $white;
        }
    }

    .title {
        position: absolute;
        top: 190px;
        width: 210px;
        max-width: 100%;
        text-align: center;
        margin: 0 auto;
        left: 0;
        right: 0;
        z-index: 2;
        font-size: 0.875rem;
        line-height: 1em;
        text-transform: uppercase;
        letter-spacing: 0.025em;
        font-weight: 400;
        font-family: $effra-font-stack;
        background-color: $sage;
        color: $slate;
        padding: 5px 10px;
        border-radius: 3px;
    }

    .subtitle {
        min-height: 90px;
        width: 100%;
        padding: 10px 0 20px 0;
        text-align: center;
        font-size: 0.85rem;
        order: -1;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .no-result {
        min-height: 434px;
        background: $white;
        padding: 40px;
        border: 1px solid $grey-200;
        flex-shrink: 0;
        max-width: 340px;
        // margin: 20px auto 0;
        text-align: center;
        display: flex;
        flex-wrap: wrap;
        align-content: center;
        align-items: center;
        justify-content: center;
    }

    .stone-preview {
        background: $stone-2;
        border: 1px solid $grey-200;
        flex-shrink: 0;
        max-width: 340px;
        margin: 0 auto;
        cursor: initial;

        :deep(.image-wrapper) {
            background: $white;
            height: 200px;
            display: flex;
            align-items: center;
            justify-content: center;

            .image {
                display: flex;
                align-items: center;
                justify-content: center;
            }

            img {
                object-fit: contain;
                height: 170px;
            }
        }

        :deep(.text-wrapper) {
            padding-top: 0;
        }

        :deep(.details) {
            padding: 20px 15px;

            .price {
                margin-right: 15px;
            }

            .subtitle {
                margin-bottom: 10px;
            }

            .th-button.small {
                padding-left: 15px;
                padding-right: 15px;
            }
        }
    }
}
</style>
